/**
 * `<RewindView/>` — the as-of-date rewind surface (B3.3, the second hero).
 *
 * Issue #764 (Phase 3). Scrub the whole task tree back to how it stood on any
 * past day. The view composes three things, top to bottom:
 *
 *   1. The rewind chrome — a "viewing: <date>" badge + a "▶ Today / Live"
 *      button that returns to the live editor, plus an "as of <date>" banner
 *      explaining what's shown (nodes created after that day are absent;
 *      states reflect that day).
 *   2. The read-only tree canvas — the SAME `<TaskCanvas/>` the live editor
 *      uses, fed the past node set (via the as-of adapter → `buildTree`) in
 *      `readOnly` mode: no edit affordances, no mutations, just an inert,
 *      explorable picture of the plan as it stood.
 *   3. The scrubber — the hero interaction: a day slider; dragging it
 *      refetches the as-of tree and re-renders the canvas as it stood then.
 *
 * Faithful to `B3-3-rewind.svg`: the amber "viewing" badge + banner, the
 * teal "Today / Live" button, the read-only canvas, and the bottom scrubber.
 *
 * Editorial revamp (#1043, Wave 2): theme-aware via `useMapTheme()`. The flag
 * stays OFF for every team (default LEGACY) until the whole revamp lands, so:
 *   - LEGACY (flag off): the shipped look — amber `#FEF3C7` viewing badge +
 *     banner, the teal solid "Today / Live" button. Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `V_B33` chrome — a mono `viewing:
 *     <date> ▾` neutral chip + a `.t-btn.ghost` "▶ Today / Live", a
 *     `.t-banner.amber` REWIND strip ("Tree shown as it stood on <date> …"),
 *     and ghost "— doesn't exist yet —" placeholders. The MapCanvas the
 *     surface renders is ALREADY editorial (Wave 1) — only the rewind chrome
 *     re-skins here. RE-SKIN, not rebuild: every `data-testid`, copy, and the
 *     scrub / as-of resolution behaviour are unchanged.
 *
 * Graceful degradation: the read endpoint may not be deployed yet — a 404 (or
 * any error) renders a calm "Rewind isn't available yet" state, never a crash.
 * An empty node set at an early day renders the origin/empty state.
 *
 * Brand firewall (§A9): the rewind axis is the PLAN's trajectory, not
 * people's. There is deliberately no "who did what" overlay, no per-person
 * activity reconstruction — provenance lives on individual nodes (B3.1), never
 * aggregated about a person. All copy is factual + calm.
 *
 * The view holds its own UI state (chosen day offset, collapse set, play
 * toggle); the orchestrating `<MapEditor/>` owns only whether the rewind is
 * open. Clean separation: data hook ⟂ pure timeline maths ⟂ this view.
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import { useMemo, useState } from "react";

import { useIsMobile } from "../../lib/useMediaQuery";
import { OutlineEditor } from "../components/OutlineEditor";
import { TaskCanvas } from "../components/TaskCanvas";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { buildTree } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";
import { RewindScrubber } from "./RewindScrubber";
import { adaptAsOfNodes } from "./asOfAdapter";
import {
  buildRewindTimeline,
  dayOffsetToDate,
  dayOffsetToQueryISO,
  formatDayLabel,
  isAtNow,
} from "./rewindTimeline";
import { useAsOfTree } from "./useAsOfTree";

const AMBER_BG = "#FEF3C7";
const AMBER_INK = "#B45309";

/** Default look-back window when no explicit earliest date is supplied. */
const DEFAULT_LOOKBACK_DAYS = 30;

export interface RewindViewProps {
  teamId: string | undefined;
  /** The team's roster, for assignee avatars (shared with the live editor). */
  members: TaskMember[];
  /** Return to the live editor (the "Today / Live" affordance). */
  onExit: () => void;
  /**
   * The earliest selectable day (the plan's start). Optional — defaults to a
   * 30-day look-back from now, which keeps the surface self-contained without
   * a second metadata fetch. A future caller can pass the team's true start.
   */
  earliestDate?: Date;
  /** "Now" — overridable for deterministic tests; defaults to the live instant. */
  now?: Date;
}

export function RewindView(props: RewindViewProps) {
  const { teamId, members, onExit, earliestDate, now } = props;

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // M4 (#1043, Wave 6) — on a phone in editorial, the past tree shows as a
  // read-only OUTLINE (the fixed-coordinate canvas pans badly on a phone), with
  // a compact scrubber + ‹ prev-day / ▶ Today buttons (wireframe `V_M4`).
  // Desktop / legacy / mobile-flag-OFF keep the read-only canvas (unchanged).
  const isMobile = useIsMobile();
  const mobileEditorial = isMobile && editorial;

  // The timeline is stable for the life of the surface (one "now"); we memo it
  // off the inputs so the scrubber math + the query date stay consistent.
  const nowInstant = useMemo(() => now ?? new Date(), [now]);
  const timeline = useMemo(() => {
    const from =
      earliestDate ?? new Date(nowInstant.getTime() - DEFAULT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
    return buildRewindTimeline(from, nowInstant);
  }, [earliestDate, nowInstant]);

  // The chosen day starts at "now" (the live tree) so the surface opens on the
  // present and the user scrubs back — calm, no jarring jump.
  const [dayOffset, setDayOffset] = useState(timeline.totalDays);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [playing, setPlaying] = useState(false);

  const atNow = isAtNow(timeline, dayOffset);
  const selectedDate = dayOffsetToDate(timeline, dayOffset);
  const queryISO = dayOffsetToQueryISO(timeline, dayOffset, nowInstant);

  const { nodes, isLoading, isError } = useAsOfTree(teamId, queryISO);

  // Adapt the replay shape → the canvas pipeline (drop archived-then nodes).
  const roots = useMemo(() => buildTree(adaptAsOfNodes(nodes)), [nodes]);
  const hasNodes = roots.length > 0;

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onScrub = (next: number) => {
    setDayOffset(next);
    // A manual scrub stops any running play-walk (the user took the wheel).
    if (playing && next !== dayOffset) setPlaying(false);
  };

  const dateLabel = atNow ? "now" : formatDayLabel(selectedDate);

  return (
    <div data-testid="rewind-view" className="relative flex h-[calc(100vh-8rem)] flex-col">
      {/* Rewind chrome: the "viewing" badge/chip + the return-to-live button.
          Editorial (`V_B33`): a mono `viewing: <date> ▾` neutral chip + a
          ghost `.t-btn` "Today / Live". Legacy: the amber viewing badge + the
          teal solid button (byte-identical to today). */}
      <div
        className="flex items-center justify-between gap-3 border-b px-4 py-2"
        style={{
          borderColor: editorial ? c.rule : PALETTE.softBorder,
          backgroundColor: editorial ? c.cardBg : PALETTE.canvas,
        }}
      >
        {editorial ? (
          <span
            data-testid="rewind-viewing-badge"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px]"
            style={{ backgroundColor: c.rollupTrack, color: c.slate, fontFamily: theme.fonts.mono }}
          >
            viewing: {dateLabel} ▾
          </span>
        ) : (
          <span
            data-testid="rewind-viewing-badge"
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold"
            style={{ backgroundColor: AMBER_BG, borderColor: "#D97706", color: AMBER_INK }}
          >
            <span aria-hidden>⟲</span> viewing: {dateLabel}
          </span>
        )}
        {editorial ? (
          <button
            type="button"
            data-testid="rewind-back-to-live"
            onClick={onExit}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:opacity-90"
            style={{ borderColor: c.rule2, color: c.ink, backgroundColor: "transparent" }}
          >
            ▶ Today / Live
          </button>
        ) : (
          <button
            type="button"
            data-testid="rewind-back-to-live"
            onClick={onExit}
            className="rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: PALETTE.teal }}
          >
            ▶ Today / Live
          </button>
        )}
      </div>

      {/* "as of <date>" banner — what the past tree shows (factual).
          Editorial (`V_B33`): a `.t-banner.amber` strip led by a mono REWIND
          issue tag. Legacy: the amber `#FEF3C7` banner (byte-identical). */}
      {editorial ? (
        <div
          data-testid="rewind-banner"
          className="border-b px-4 py-2 text-xs"
          style={{
            borderColor: "rgba(201,133,45,0.4)",
            backgroundColor: theme.stateStyle("blocked").chipBg,
            color: c.slate,
          }}
        >
          <span
            className="mr-2 text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
          >
            REWIND
          </span>
          {atNow
            ? "This is the live map. Scrub back to see how it stood on an earlier day."
            : `Tree shown as it stood on ${dateLabel} — nodes created after this date are not shown; states reflect that day.`}
        </div>
      ) : (
        <div
          data-testid="rewind-banner"
          className="border-b px-4 py-1.5 text-[11px]"
          style={{ borderColor: "#D97706", backgroundColor: AMBER_BG, color: AMBER_INK }}
        >
          <span aria-hidden>● </span>
          {atNow
            ? "This is the live map. Scrub back to see how it stood on an earlier day."
            : `Tree shown as it stood on ${dateLabel} — tasks created after this day are not shown; states reflect that day.`}
        </div>
      )}

      {/* The canvas — read-only, fed the past node set. The canvas itself is
          already editorial (Wave 1); only the chrome around it re-skins. */}
      <div className="relative min-h-0 flex-1">
        {isError ? (
          <div
            data-testid="rewind-unavailable"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            <div className="text-sm font-semibold">Rewind isn’t available yet</div>
            <div className="text-xs" style={{ color: c.faint }}>
              The map’s history will be ready here soon.
            </div>
          </div>
        ) : isLoading ? (
          <div
            data-testid="rewind-loading"
            className="flex h-full items-center justify-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            Rewinding the map…
          </div>
        ) : hasNodes ? (
          <>
            {mobileEditorial ? (
              // M4 (`V_M4`) — the past tree as a read-only editorial OUTLINE on a
              // phone. The outline scrolls cleanly in a narrow viewport where the
              // fixed-coordinate canvas pans badly. Inert handlers keep it
              // read-only (no mutation path), mirroring how the canvas is fed
              // no-op callbacks above; tapping a row is a no-op (rewind has no
              // drawer). Same as-of `roots` source — only the renderer differs.
              <div className="h-full overflow-auto" data-testid="rewind-mobile-outline">
                <OutlineEditor
                  compact
                  roots={roots}
                  members={members}
                  selectedId={null}
                  editingNodeId={null}
                  onOpenNode={() => undefined}
                  onSetEditing={() => undefined}
                  onRename={() => undefined}
                  onCreate={() => undefined}
                  onMove={() => undefined}
                  onReorder={() => undefined}
                  onAssignToggle={() => undefined}
                  onRemove={() => undefined}
                />
              </div>
            ) : (
              <TaskCanvas
                readOnly
                roots={roots}
                members={members}
                collapsed={collapsed}
                selectedId={null}
                onSelect={() => undefined}
                onToggleCollapse={toggleCollapse}
                onSetState={() => undefined}
                onAddSubtask={() => undefined}
                onRename={() => undefined}
                onArchive={() => undefined}
                onAssignToggle={() => undefined}
                onOpenDrawer={() => undefined}
              />
            )}
            {/* Editorial ghost hint (`V_B33`): a calm, mono "— doesn't exist
                yet —" cue that the past tree omits later branches. Faithful to
                the wireframe; decorative + non-interactive, so it never alters
                the canvas. Legacy renders nothing here (byte-identical). */}
            {editorial && !atNow && (
              <div
                data-testid="rewind-ghost-hint"
                aria-hidden
                className="pointer-events-none absolute bottom-3 right-4 text-[10px] italic"
                style={{ fontFamily: theme.fonts.mono, color: c.faint }}
              >
                later branches — doesn’t exist yet —
              </div>
            )}
          </>
        ) : (
          <div
            data-testid="rewind-empty"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            <div className="text-sm font-semibold">Nothing on the map yet on {dateLabel}</div>
            <div className="text-xs" style={{ color: c.faint }}>
              The plan hadn’t been started on this day. Scrub forward to watch it take shape.
            </div>
          </div>
        )}
      </div>

      {/* The scrubber — the hero interaction. */}
      <RewindScrubber
        timeline={timeline}
        dayOffset={dayOffset}
        onChange={onScrub}
        playing={playing}
        onTogglePlay={() => setPlaying((v) => !v)}
      />

      {/* M4 mobile day controls (`V_M4`) — a thumb-reach ‹ prev-day step + a
          filled ▶ Today jump beneath the compact scrubber. Phone + editorial
          only; desktop / legacy never render this (the scrubber + play cover the
          desktop interaction). */}
      {mobileEditorial && (
        <div
          data-testid="rewind-mobile-controls"
          className="flex items-center gap-2 border-t px-4 py-3"
          style={{ borderColor: c.rule, backgroundColor: c.bgRecess }}
        >
          <button
            type="button"
            data-testid="rewind-mobile-prev"
            onClick={() => onScrub(Math.max(0, dayOffset - 1))}
            disabled={dayOffset <= 0}
            className="flex flex-1 min-h-tap items-center justify-center rounded-md border text-xs transition-colors disabled:opacity-40"
            style={{ borderColor: c.rule2, color: c.ink, backgroundColor: c.cardBg }}
          >
            ‹ {formatDayLabel(dayOffsetToDate(timeline, Math.max(0, dayOffset - 1)))}
          </button>
          <button
            type="button"
            data-testid="rewind-mobile-today"
            onClick={() => onScrub(timeline.totalDays)}
            disabled={atNow}
            className="flex flex-1 min-h-tap items-center justify-center rounded-md text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: c.teal, color: "#FFFFFF" }}
          >
            ▶ Today
          </button>
        </div>
      )}
    </div>
  );
}
